import { useQuery } from '@tanstack/react-query';
import type { paths } from '@bastion/api';
import { authenticatedFetch } from '../authenticatedFetch';
import type { EpisodeQueryFilters } from '../types';
import { resolveReviewedAgainstFilters } from './reviewedAgainstFilters';
import { EPISODE_CHARTS_QUERY_KEY } from './episodeCollectionQueries';
import {
  buildEpisodeFilterParams,
  episodeCountFilters,
  uniqueNonEmptyIds,
} from './useCloudEpisodes';

export type EpisodeCharts =
  paths['/episodes/charts']['get']['responses']['200']['content']['application/json'];

const CHART_ID_CHUNK_SIZE = 80;
const EMPTY_CHARTS: EpisodeCharts = {
  total_count: 0,
  total_seconds: 0,
  volume: [],
  ratings: [0, 3, 5, null].map((rating) => ({ rating, count: 0 })),
  durations: [
    [0, 15], [15, 30], [30, 60], [60, 120], [120, 300], [300, null], [null, null],
  ].map(([min_seconds, max_seconds]) => ({ min_seconds, max_seconds, count: 0 })),
};

/** Use the list's filter builder without paging or sort settings. */
export function buildEpisodeChartsPath(
  filters: EpisodeQueryFilters = {},
  bounds?: { start: string; end: string },
): string {
  const params = buildEpisodeFilterParams(episodeCountFilters(filters));
  if (bounds) {
    params.set('volume_start', bounds.start);
    params.set('volume_end', bounds.end);
  }
  const query = params.toString();
  return query ? `/episodes/charts?${query}` : '/episodes/charts';
}

function sumCharts(charts: EpisodeCharts[]): EpisodeCharts {
  return charts.reduce<EpisodeCharts>((sum, part) => ({
    total_count: sum.total_count + part.total_count,
    total_seconds: sum.total_seconds + part.total_seconds,
    volume: part.volume.length === 0 ? sum.volume : part.volume.map((bucket, index) => ({
      ...bucket,
      count: bucket.count + (sum.volume[index]?.count ?? 0),
    })),
    ratings: part.ratings.map((bucket, index) => ({
      ...bucket,
      count: bucket.count + (sum.ratings[index]?.count ?? 0),
    })),
    durations: part.durations.map((bucket, index) => ({
      ...bucket,
      count: bucket.count + (sum.durations[index]?.count ?? 0),
    })),
  }), EMPTY_CHARTS);
}

export async function fetchEpisodeCharts(
  filters: EpisodeQueryFilters,
  signal?: AbortSignal,
): Promise<EpisodeCharts> {
  const { filters: effectiveFilters, empty } = await resolveReviewedAgainstFilters(filters, signal);
  if (empty) return EMPTY_CHARTS;
  const ids = uniqueNonEmptyIds(effectiveFilters.id);
  if (ids.length <= CHART_ID_CHUNK_SIZE) {
    return authenticatedFetch<EpisodeCharts>(buildEpisodeChartsPath(effectiveFilters), { signal });
  }
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += CHART_ID_CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + CHART_ID_CHUNK_SIZE));
  }
  const fetchChunks = (bounds?: { start: string; end: string }) => Promise.all(
    chunks.map((id) => authenticatedFetch<EpisodeCharts>(
      buildEpisodeChartsPath({ ...effectiveFilters, id }, bounds), { signal },
    )),
  );
  let results = await fetchChunks();
  const volumes = results.map((result) => result.volume).filter((volume) => volume.length > 0);
  if (volumes.length === 0) return sumCharts(results);
  const start = volumes.reduce((earliest, volume) =>
    Date.parse(volume[0].start) < Date.parse(earliest) ? volume[0].start : earliest,
  volumes[0][0].start);
  const end = volumes.reduce((latest, volume) =>
    Date.parse(volume[volume.length - 1].end) > Date.parse(latest)
      ? volume[volume.length - 1].end : latest,
  volumes[0][volumes[0].length - 1].end);
  if (results.some(({ volume }) => volume.length === 0
      || Date.parse(volume[0].start) !== Date.parse(start)
      || Date.parse(volume[volume.length - 1].end) !== Date.parse(end))) {
    // Each chunk must use the same time bins before its counts can be added.
    results = await fetchChunks({ start, end });
  }
  const reference = results.find(({ volume }) => volume.length > 0)?.volume;
  if (reference && results.some(({ volume }) => volume.length > 0 && (
    volume.length !== reference.length || volume.some((bucket, index) =>
      Date.parse(bucket.start) !== Date.parse(reference[index].start)
        || Date.parse(bucket.end) !== Date.parse(reference[index].end))
  ))) {
    throw new Error('The episode chart range changed. Refresh the page.');
  }
  return sumCharts(results);
}

export function useEpisodeCharts(
  filters: EpisodeQueryFilters = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...EPISODE_CHARTS_QUERY_KEY, episodeCountFilters(filters)],
    queryFn: ({ signal }) => fetchEpisodeCharts(filters, signal),
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    retry: false,
  });
}
